import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { ease, progress } from '@/components/landing/film/timeline'
import { fixed, setStyle } from '@/components/landing/film/overlay-utils'

interface OdometerProps {
    value: string
    start: number
    duration: number
    register: RegisterUpdater
}

const DIGITS = Array.from({ length: 40 }, (_, i) => String(i % 10))
const SPINS = 3

// Rolling counter: every digit spins down through full turns and settles on its value
const Odometer: FC<OdometerProps> = ({ value, start, duration, register }): ReactNode => {
    const stripRefs = useRef<Array<HTMLSpanElement | null>>([])
    const chars = Array.from(value)

    useEffect(() => register((t) => {
        let order = 0
        Array.from(value).forEach((char, i) => {
            const strip = stripRefs.current[i]
            if (!strip || !/\d/.test(char)) return
            const delay = order * 0.12
            order++
            const p = ease.outCubic(progress(t, start + delay, start + duration + delay))
            const position = Number(char) + 10 * SPINS * (1 - p)
            setStyle(strip, 'transform', `translateY(${fixed(-position, 3)}em)`)
        })
    }), [register, start, duration, value])

    return (
        <span className='odo' role='img' aria-label={value}>
            {chars.map((char, i) => {
                if (/\d/.test(char)) {
                    return (
                        <span key={i} className='odo-col' aria-hidden='true'>
                            <span
                                ref={(element) => {
                                    stripRefs.current[i] = element
                                }}
                                className='odo-strip'
                                style={{ transform: `translateY(-${Number(char) + 10 * SPINS}em)` }}
                            >
                                {DIGITS.map((digit, k) => (
                                    <span key={k}>
                                        {digit}
                                    </span>
                                ))}
                            </span>
                        </span>
                    )
                }

                return (
                    <span key={i} className='odo-static' aria-hidden='true'>
                        {char === ' ' ? ' ' : char}
                    </span>
                )
            })}
        </span>
    )
}

export default Odometer
